import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Instagram, Linkedin, Facebook, Save } from 'lucide-react';
import { api } from '../../services/api';
import { authService } from '../../services/auth';

const SettingsPage = () => {
  const isAdmin = authService.getUser()?.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [instagram, setInstagram] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [facebook, setFacebook] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const settings = await api.getSettings();
    if (settings) {
      setPhone(settings.phone || '');
      setEmail(settings.email || '');
      setAddress(settings.address || '');
      setInstagram(settings.socialLinks?.instagram || '');
      setLinkedin(settings.socialLinks?.linkedin || '');
      setFacebook(settings.socialLinks?.facebook || '');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const result = await api.updateSettings({
      phone,
      email,
      address,
      socialLinks: { instagram, linkedin, facebook }
    });

    if (result.success) {
      setMessage('✅ Configuración actualizada correctamente');
    } else {
      setMessage(`❌ ${result.error}`);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  const inputClass = `w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
    !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
  }`;

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Configuración</h1>
        <p className="text-gray-600">Datos de contacto y redes que se muestran en el sitio público</p>
      </div>

      {!isAdmin && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          Solo un administrador puede modificar esta configuración. Podés ver los valores actuales.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Contacto público</h2>
          <p className="text-sm text-gray-500 mb-4">
            Se muestran en la sección "¿Buscas algo específico?" y en el pie de página del sitio.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="+54 11 4567-8900"
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="info@sanzberger.com"
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dirección / Ubicación</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="Buenos Aires, Argentina"
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Redes sociales</h2>
          <p className="text-sm text-gray-500 mb-4">Enlaces que se muestran en el pie de página.</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="https://instagram.com/sanzberger"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn</label>
              <div className="relative">
                <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="https://linkedin.com/company/sanzberger"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="https://facebook.com/sanzberger"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        {message && <p className="text-sm">{message}</p>}

        {isAdmin && (
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        )}
      </form>
    </div>
  );
};

export default SettingsPage;
